import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import {
  adminListInquiries,
  adminUpdateInquiryStatus,
  adminDeleteInquiry,
  logout,
} from "../lib/api";
import { ADMIN } from "../constants/testIds";
import { Trash2, Mail, LogOut, Plus, ArrowRight } from "lucide-react";

const STATUSES = ["new", "contacted", "archived"];

const statusStyles = {
  new: "text-[var(--accent)] border-[var(--accent)]",
  contacted: "text-[var(--blue-soft)] border-[var(--blue)]",
  archived: "text-[var(--slate-dim)] border-[var(--slate-dim)]",
};

const AdminInquiries = () => {
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const navigate = useNavigate();

  const load = () => {
    setLoading(true);
    adminListInquiries()
      .then((d) => { setInquiries(d); setLoading(false); })
      .catch((e) => {
        if (e.response?.status === 401) navigate("/admin/login");
        else setLoading(false);
      });
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const updateStatus = async (id, status) => {
    await adminUpdateInquiryStatus(id, status);
    load();
  };
  const onDelete = async (id) => {
    if (!window.confirm("Delete this inquiry?")) return;
    await adminDeleteInquiry(id);
    load();
  };
  const onLogout = () => { logout(); navigate("/admin/login"); };

  return (
    <Layout>
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-8">
          <div>
            <div className="font-mono-label text-[var(--slate)] mb-3">Admin · Inquiries</div>
            <h1 className="font-display text-4xl md:text-5xl text-[var(--ivory)]">
              Private correspondence.
            </h1>
          </div>
          <div className="flex gap-3">
            <Link to="/admin" className="btn-ghost" data-testid={ADMIN.reportsTab}>
              <ArrowRight size={14} /> Reports
            </Link>
            <button onClick={onLogout} className="btn-ghost" data-testid={ADMIN.logout}>
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>

        <div className="divider-rule mb-10" />

        {loading ? (
          <div className="font-mono-label text-[var(--slate)] py-12 text-center">Loading…</div>
        ) : inquiries.length === 0 ? (
          <div className="border border-[var(--border)] p-16 text-center">
            <Mail className="mx-auto text-[var(--slate-dim)] mb-4" size={28} />
            <div className="font-mono-label text-[var(--slate)] mb-2">No inquiries yet</div>
            <p className="text-[var(--slate-dim)] text-sm">
              Form submissions from the website will appear here.
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-white/5 border-y border-white/5">
            {inquiries.map((q) => (
              <li key={q.id} className="py-6" data-testid={ADMIN.inquiryRow(q.id)}>
                <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-start">
                  <div className="md:col-span-3">
                    <div className="font-display text-xl text-[var(--ivory)]">{q.name}</div>
                    <a href={`mailto:${q.email}`} className="text-[var(--slate)] text-sm editorial-link">
                      {q.email}
                    </a>
                    {q.company && <div className="text-xs text-[var(--slate-dim)] mt-1">{q.company}</div>}
                  </div>
                  <div className="md:col-span-6">
                    {q.report_title && (
                      <div className="font-mono-label text-[var(--accent)] mb-2">
                        Re: {q.report_title}
                      </div>
                    )}
                    <button
                      onClick={() => setExpanded(expanded === q.id ? null : q.id)}
                      className="text-left text-[var(--ivory)] text-sm leading-relaxed hover:text-[var(--accent)] transition-colors"
                    >
                      {expanded === q.id ? (
                        <span className="whitespace-pre-wrap">{q.message}</span>
                      ) : (
                        <>
                          {q.message.slice(0, 180)}
                          {q.message.length > 180 && <span className="text-[var(--accent)]"> · read more</span>}
                        </>
                      )}
                    </button>
                    <div className="font-mono-label text-[var(--slate-dim)] mt-3">
                      {new Date(q.created_at).toLocaleString()}
                      {q.email_id && <span className="ml-3 text-[var(--accent)]">· email delivered</span>}
                    </div>
                  </div>
                  <div className="md:col-span-3 flex md:justify-end items-start gap-2 flex-wrap">
                    {STATUSES.map((s) => (
                      <button
                        key={s}
                        onClick={() => updateStatus(q.id, s)}
                        className={`px-3 py-1.5 border font-mono-label transition-colors ${
                          q.status === s
                            ? statusStyles[s]
                            : "text-[var(--slate-dim)] border-[var(--border)] hover:text-[var(--ivory)] hover:border-[var(--ivory)]"
                        }`}
                        data-testid={`${ADMIN.inquiryStatus(q.id)}-${s}`}
                      >
                        {s}
                      </button>
                    ))}
                    <button
                      onClick={() => onDelete(q.id)}
                      className="p-2 border border-[var(--border)] hover:border-red-400 text-[var(--slate)] hover:text-red-300 transition-colors"
                      data-testid={ADMIN.inquiryDelete(q.id)}
                      title="Delete"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </Layout>
  );
};

export default AdminInquiries;
